import { useContext, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { Typography } from 'design-system/src/components/Typography/Typography';
import Button from 'design-system/src/components/Button/Button';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';
import { Badge } from 'design-system/src/components/Badge/Badge';
import { Toast } from 'design-system/src/components/Toast/Toast';

import { TremfyaExpressEnrollmentTask, TaskEvent } from '../../../api';
import { TimelineItemList, TimelineItem } from '../../TimelineItem/TimelineItem';
import { Sidebar, SidebarTopBar } from '../../sidebar/Sidebar';
import { DrugPanel, PatientPanel, PrescriberPanel } from '../../sidebar/SidebarPanels';
import { TaskContext } from '../contexts';
import { ItemViewTopNav } from '../../tableItemDetailViewComponents/ItemViewTopNav';
import {
  BodyContainer,
  TopNavAndContent,
  ContentOuterWrapper,
  CenteredContent,
} from '../../tableItemDetailViewComponents/Containers';
import { GeneralInfoTable } from '../../GeneralInfoTable/GeneralInfoTable';
import DeclineModal from '../DeclineModal';
import TremfyaExpressEnrollmentCoreForm from './TremfyaExpressEnrollmentCoreForm.tsx';

/**
 * Version of TaskView that is used for Tremfya Express Enrollment tasks.
 */
const TremfyaExpressEnrollmentTaskView: React.FC = () => {
  const { taskQuery, taskId, onTaskUpdate } = useContext(TaskContext);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  // tracking submission state manually because the actual submit mutation is in a child component
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  let body;
  let declineModal;
  if (taskQuery.isLoading) {
    body = <LoadingSpinner />;
  } else if (taskQuery.error) {
    body = <PageLoadError />;
  } else if (taskQuery.data) {
    const { drug, patient, prescriber } = taskQuery.data;
    const task = taskQuery.data.task as TremfyaExpressEnrollmentTask;

    const onSuccess = () => {
      onTaskUpdate();
      toast.custom(() => <Toast variant="success">Enrollment submitted</Toast>);
      navigate('/tasks');
    };

    let completedTimeline = null;
    if (task.status === 'completed') {
      if (task.timeline.length < 2) {
        Sentry.captureMessage('No completion event found for Tremfya Express Enrollment task', {
          level: 'error',
          extra: {
            taskId,
          },
        });
        return <PageLoadError />;
      }
      const completionEvent = task.timeline[1] as TaskEvent;
      completedTimeline = (
        <TimelineItemList>
          <TimelineItem
            author={completionEvent.user}
            timestamp={completionEvent.event_timestamp}
            action={completionEvent.action}
            message={completionEvent.label}
            isLastEventInList={true}
          />
        </TimelineItemList>
      );
    }

    const declineButton = (
      <Button
        type="button"
        size="sm"
        disabled={isSubmitting}
        variant="tertiary"
        onClick={() => setDeclineModalOpen(true)}
      >
        Decline enrollment
      </Button>
    );

    declineModal = (
      <DeclineModal
        closeModal={() => setDeclineModalOpen(false)}
        patient={patient}
        drug={drug}
        taskType={task.type}
        taskId={taskId}
        onTaskUpdate={onTaskUpdate}
      />
    );

    body = (
      <BodyContainer>
        <TopNavAndContent>
          <ItemViewTopNav
            isSidebarCollapsed={isSidebarCollapsed}
            setIsSidebarCollapsed={setIsSidebarCollapsed}
            additionalRightSideContent={task.status === 'completed' && <Badge variant="neutral">Closed</Badge>}
          />

          <ContentOuterWrapper>
            <CenteredContent>
              <Typography styledAs="h6" renderedAs="h1" marginBottom="1rem">
                {task.type}
              </Typography>
              <GeneralInfoTable
                patientName={drug.patient_name}
                prescriberName={drug.prescriber_name}
                dob={drug.dob}
                prescribedOn={drug.prescription_recieved_at}
                drugName={drug.name}
                prescriptionId={drug.id}
              />

              {task.status === 'completed' ? (
                completedTimeline
              ) : (
                <TremfyaExpressEnrollmentCoreForm
                  drug={drug}
                  prescriber={prescriber}
                  formData={task.form_data}
                  declineButton={declineButton}
                  onSuccess={onSuccess}
                  setIsSubmitting={setIsSubmitting}
                  submissionEndpoint={`tasks/${taskId}/complete-tremfya-enrollment`}
                />
              )}
            </CenteredContent>
          </ContentOuterWrapper>
        </TopNavAndContent>

        {!isSidebarCollapsed && (
          <Sidebar>
            <SidebarTopBar />
            <DrugPanel drug={drug} showPharmaRelationshipNotice />
            <PatientPanel patient={patient} />
            <PrescriberPanel prescriber={prescriber} />
          </Sidebar>
        )}
      </BodyContainer>
    );
  }

  return (
    <>
      {body}
      {declineModalOpen && declineModal}
    </>
  );
};

export default TremfyaExpressEnrollmentTaskView;
